import React, { useEffect, useRef } from 'react';

export default function ExitScreen({ onComplete }) {
  const audioRef = useRef(null);

  useEffect(() => {
    // Play exit sound
    const exitSound = new Audio('/assets/audio/exit-sound.mp3');
    exitSound.volume = 1.0; // Maximum volume
    audioRef.current = exitSound;
    exitSound.play().catch(e => console.log('Exit sound play failed:', e));
    console.log('🎵 Playing exit sound');

    // Auto-complete after 10 seconds
    const timer = setTimeout(() => {
      onComplete();
    }, 10000);

    // ⭐ ESC key listener to skip exit screen
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        console.log('⏭️ ESC pressed - skipping exit screen');
        clearTimeout(timer);
        onComplete();
      }
    };

    window.addEventListener('keydown', handleEscape);

    // Cleanup
    return () => {
      clearTimeout(timer);
      window.removeEventListener('keydown', handleEscape);
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [onComplete]);

  // 📱 Mobile skip handler - tappable equivalent of ESC since phones lack a keyboard
  const handleSkip = () => {
    console.log('⏭️ Skip tapped - skipping exit screen');
    onComplete();
  };

  return (
    <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black exit-screen-container">
      {/* Exit Badge - 150% size (1.5x larger) */}
      <div className="exit-badge-fade">
        <img 
          src="/assets/gtnw3-exit-badge.png" 
          alt="GTNW3"
          className="w-full h-auto px-4"
          style={{
            maxWidth: '1350px', // 150% size (1.5x of ~900px base)
            filter: `
              drop-shadow(0 0 40px rgba(255, 100, 0, 0.8))
              drop-shadow(0 0 80px rgba(255, 69, 0, 0.6))
              drop-shadow(0 4px 16px rgba(0, 0, 0, 0.9))
            `,
          }}
        />
      </div>

      {/* Skip hint — tappable on mobile, ESC works on desktop */}
      <button
        onClick={handleSkip}
        className="absolute bottom-8 right-8 text-gray-400 hover:text-white text-sm md:text-base animate-pulse bg-black/40 hover:bg-black/70 backdrop-blur-sm border border-gray-600 rounded-md px-4 py-2 cursor-pointer transition-colors"
        data-testid="exit-screen-skip-btn"
      >
        TAP TO SKIP <span className="hidden md:inline">(ESC)</span>
      </button>

      <style>{`
        .exit-screen-container {
          animation: screenShake 10s ease-in-out forwards;
        }

        .exit-badge-fade {
          animation: exitFadeInOut 10s ease-in-out forwards;
        }

        @keyframes exitFadeInOut {
          0% { 
            opacity: 0; 
            transform: scale(0.6);
          }
          65% { 
            opacity: 1; 
            transform: scale(1);
          }
          90% { 
            opacity: 1; 
            transform: scale(1);
          }
          100% { 
            opacity: 0; 
            transform: scale(0.95);
          }
        }

        @keyframes screenShake {
          0%, 74% {
            transform: translate(0, 0);
          }
          /* ⭐ VIOLENT SHAKE STARTS at 75% (7.5s) - MORE AGGRESSIVE & FASTER */
          75% {
            transform: translate(-10px, 6px);
          }
          76% {
            transform: translate(8px, -7px);
          }
          77% {
            transform: translate(-9px, 8px);
          }
          78% {
            transform: translate(10px, -6px);
          }
          79% {
            transform: translate(-7px, 9px);
          }
          80% {
            transform: translate(9px, -8px);
          }
          81% {
            transform: translate(-8px, 7px);
          }
          82% {
            transform: translate(7px, -9px);
          }
          83% {
            transform: translate(-10px, 6px);
          }
          84% {
            transform: translate(8px, -8px);
          }
          85% {
            transform: translate(-6px, 10px);
          }
          86% {
            transform: translate(9px, -7px);
          }
          87% {
            transform: translate(-7px, 8px);
          }
          88% {
            transform: translate(8px, -6px);
          }
          89% {
            transform: translate(-9px, 7px);
          }
          90% {
            transform: translate(6px, -9px);
          }
          91% {
            transform: translate(-8px, 8px);
          }
          92% {
            transform: translate(7px, -7px);
          }
          93% {
            transform: translate(-6px, 6px);
          }
          94% {
            transform: translate(5px, -5px);
          }
          95% {
            transform: translate(-4px, 4px);
          }
          96% {
            transform: translate(3px, -3px);
          }
          97% {
            transform: translate(-2px, 2px);
          }
          98% {
            transform: translate(1px, -1px);
          }
          99% {
            transform: translate(-1px, 1px);
          }
          100% {
            transform: translate(0, 0);
          }
        }
      `}</style>
    </div>
  );
}
